import { Modal } from "@mantine/core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import type { WizardModeProps } from "../../types/wizard.types";
import { FIELD_WIZARD_REGISTRY } from "./wizardRegistry";
import type { WizardKey, WizardPayloadMap } from "./wizardRegistry";

type OpenWizardOptions<K extends WizardKey> =
  | { mode: "create"; onSuccess: (object: WizardPayloadMap[K]) => void }
  | {
      mode: "edit";
      objectId: number;
      onSuccess?: (object: WizardPayloadMap[K]) => void;
    };

type WizardStackEntry = {
  id: number;
  isOpen: boolean;
  wizardKey: WizardKey;
  modeProps: WizardModeProps;
  onNestedSave: (object: unknown) => void;
};

type WizardStackContextValue = {
  /**
   * Number of open stacked wizards. Every wizard root modal passes
   * `closeOnEscape={depth === 0}` so one Escape above a nested wizard doesn't
   * also close the root buried beneath it. Mantine overrides `closeOnEscape` on
   * a modal that carries a `stackId` and sits in a `Modal.Stack`, so the guard
   * is dead once nested and live only standalone — hence safe to apply
   * uniformly to any root modal without per-wizard reasoning.
   */
  depth: number;
  openWizard: <K extends WizardKey>(
    wizardKey: K,
    options: OpenWizardOptions<K>
  ) => void;
};

// Depth 0 outside a provider is deliberate: the MSM companies page mounts a
// wizard with no provider above it and needs normal standalone Escape.
const DEFAULT_CONTEXT_VALUE: WizardStackContextValue = {
  depth: 0,
  openWizard: () => {
    if (import.meta.env.DEV) {
      console.error(
        "openWizard() was called with no WizardStackProvider above it — the `+` button will do nothing."
      );
    }
  }
};

const WizardStackContext = createContext<WizardStackContextValue>(
  DEFAULT_CONTEXT_VALUE
);

function useWizardStack() {
  return useContext(WizardStackContext);
}

type WizardStackProviderProps = {
  children: React.ReactNode;
  siteId: number;
  variantId: number;
};

function WizardStackProvider({
  children,
  siteId,
  variantId
}: WizardStackProviderProps) {
  const [stack, setStack] = useState<Array<WizardStackEntry>>([]);
  const nextIdRef = useRef(0);

  const openWizard = useCallback(
    <K extends WizardKey>(wizardKey: K, options: OpenWizardOptions<K>) => {
      nextIdRef.current += 1;

      const modeProps: WizardModeProps =
        options.mode === "create"
          ? { mode: "create" }
          : { mode: "edit", objectId: options.objectId };
      const onSuccess = options.onSuccess;

      setStack((prev) => [
        ...prev,
        {
          id: nextIdRef.current,
          isOpen: true,
          wizardKey,
          modeProps,
          onNestedSave: (object: unknown) => {
            onSuccess?.(object as WizardPayloadMap[K]);
          }
        }
      ]);
    },
    []
  );

  // A Mantine `Modal` de-registers from `Modal.Stack` in an effect on `opened`,
  // not on unmount, so removing an entry while still open leaves a phantom
  // current-id that hides the modal underneath. Close first, prune below.
  const closeEntry = useCallback((id: number) => {
    setStack((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, isOpen: false } : entry
      )
    );
  }, []);

  useEffect(() => {
    setStack((prev) => {
      const hasClosedEntry = prev.some((entry) => !entry.isOpen);
      return hasClosedEntry ? prev.filter((entry) => entry.isOpen) : prev;
    });
  }, [stack]);

  const openEntryCount = stack.filter((entry) => entry.isOpen).length;

  const contextValue = useMemo<WizardStackContextValue>(
    () => ({ depth: openEntryCount, openWizard }),
    [openEntryCount, openWizard]
  );

  return (
    <WizardStackContext.Provider value={contextValue}>
      {children}
      <Modal.Stack>
        {stack.map((entry) => {
          const { Component } = FIELD_WIZARD_REGISTRY[entry.wizardKey];

          return (
            <Component
              isOpen={entry.isOpen}
              key={entry.id}
              modeProps={entry.modeProps}
              siteId={siteId}
              stackId={String(entry.id)}
              variantId={variantId}
              onClose={() => closeEntry(entry.id)}
              onNestedSave={entry.onNestedSave}
            />
          );
        })}
      </Modal.Stack>
    </WizardStackContext.Provider>
  );
}

export { WizardStackProvider, useWizardStack };
